import { computed, get } from "@ember/object"
import Service, { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import config from "cc-frontend/config/environment"
import Cookies from "js-cookie"
import { assign } from "lodash-es"
import ENV from "../config/environment"

export default class Ajax extends Service {
  @service session

  @computed("session.token")
  get headers() {
    // console.log('HI', this.get('session.session.attributes.token'))
    let headers = {
      "Content-Type": "application/json",
    }
    let authToken = this.get("session.token")
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`
      headers["X-SESSION-ID"] = get(this, "session.id")
      headers["X-CLIENT-VERSION"] = config.CLIENT_VERSION
      // headers["sentry-trace"] =
    }
    return headers
  }

  logResponse(method, response) {
    Sentry.setTag("requestId", response.headers.get("x-request-id"))
    Sentry.addBreadcrumb({
      // message: response.url,
      category: "xhr",
      type: "http",
      data: {
        method: method,
        url: response.url,
        status_code: response.status,
        "x-request-id": response.headers.get("x-request-id"),
      },
    })
  }

  request(url, opts = {}) {
    return fetch(
      url,
      Object.assign({}, opts, {
        headers: Object.assign({}, get(this, "headers"), opts.headers),
      })
    )
      .then((response) => {
        this.logResponse("GET", response)
        let requestId = get(response, "headers.map.x-request-id")
        return response.text().then((body) => {
          try {
            return JSON.parse(body)
          } catch (e) {
            Sentry.withScope(function (scope) {
              scope.setContext("Response Data", {
                url: url,
                status: response.status,
                body: body,
              })
              console.log("Response body", body)
              console.error(
                `Error parsing JSON: ${response.status}. ${url}. RequestId: ${requestId}`,
                e
              )
              Sentry.captureException(e)
            })
            // Rethrow so it's caught again
            throw e
          }
        })
      })
      .catch((error) => {
        console.log("FAILED RESPONSE", error)
        // This didn't prove to be useful and just ran up our exception budget in our error monitoring.
        // I always ignored these b/c the signal to noise ratio was too low.
        // Sentry.withScope((scope) => {
        //   scope.setContext("Response Data", {
        //     url: url,
        //     error: error,
        //   })
        // Sentry.captureMessage(`Failed Response: ${url}`, "error")
        // })

        return error
      })
  }

  /**
   * I created this because I needed way in the store to get at what type of error it is.
   * This let's that happen easily.
   *
   * The problem is `catch` only happesn for errors, but 4xx responses aren't errors so they
   * return as if they're 2xx errors. That was causing issues where we were trying to parse
   * something we thought was a successful JSON response but was a "Too many requests" string
   *
   * @param {*} url
   * @param {*} opts
   * @return ["ok", response] | ["error", error]
   */
  requestFromAdapter(url, opts = {}) {
    return fetch(
      url,
      Object.assign({}, opts, {
        headers: Object.assign({}, get(this, "headers"), opts.headers),
      })
    )
      .then((response) => {
        this.logResponse("GET", response)
        let requestId = response.headers.get("x-request-id")
        let responseDataContext = {
          url: url,
          status: response.status,
          requestId: requestId,
        }
        return response.text().then((body) => {
          if (response.status < 200 || response.status > 299) {
            console.error(`Non 2xx response: ${response.status}. ${url}. RequestId: ${requestId}`)
            // We don't send over the body at the top as we don't always want to send the body to Sentry --
            // that would be a waste of data. instead, we only send it on non 2xx responses and errors
            // Also, log the cookies as we're getting 431 and it's the most likely culprit

            // If it's a 429 (too many requests), we don't  worry as we will refetch that
            if (response.status !== 429 && response !== 401) {
              Sentry.withScope((scope) => {
                scope.setTag("requestId", requestId)
                scope.setContext(
                  "Response Data",
                  assign({}, responseDataContext, {
                    response: body,
                    cookies: Cookies.get(),
                  })
                )
                Sentry.captureMessage(`${response.status} response: ${url}`)
              })
            }
            return ["error", { status: response.status }]
          } else {
            try {
              let json = JSON.parse(body)
              return ["ok", json]
            } catch (e) {
              console.log("Reponse body", body)
              console.error(
                `Error parsing JSON: ${response.status}. ${url}. RequestId: ${requestId}`
              )
              // We don't do this at the top as we don't always want to send the body to Sentry --
              // that would be a waste of data. instead, we only send it on non 2xx responses and errors
              Sentry.withScope((scope) => {
                scope.setContext(
                  "Response Data",
                  assign({}, responseDataContext, {
                    response: body,
                    cookies: Cookies.get(),
                  })
                )
                Sentry.captureException(e)
              })
              throw e
            }
          }
        })
      })
      .catch((error) => {
        // We don't need to log aborted responses
        if (error.name !== "AbortError") {
          console.log("FAILED RESPONSE", error)
          // Sentry.captureException(error)
        }
        return ["error", error]
      })
  }

  requestRaw(url, opts) {
    return fetch(url, Object.assign({}, opts, { headers: get(this, "headers") }))
      .then((response) => {
        this.logResponse("GET", response)
        return response
      })
      .catch((error) => {
        // We don't need to log aborted responses
        if (error.name !== "AbortError") {
          console.log("FAILED RESPONSE", error)
          // Sentry.captureException(error)
        }
        return error
      })
  }

  post(url, opts) {
    return fetch(
      url,
      Object.assign({}, opts, { method: "POST", headers: get(this, "headers") })
    ).then((response) => {
      this.logResponse("POST", response)
      return response.json()
    })
  }

  postRaw(url, opts) {
    return fetch(
      url,
      Object.assign({}, opts, { method: "POST", headers: get(this, "headers") })
    ).then((response) => {
      this.logResponse("POST", response)
      return response
    })
  }

  put(url, opts) {
    return fetch(
      url,
      Object.assign({}, opts, { method: "PUT", headers: get(this, "headers") })
    ).then((response) => {
      this.logResponse("PUT", response)
      return response.json()
    })
  }

  putRaw(url, opts) {
    return fetch(
      url,
      Object.assign({}, opts, { method: "PUT", headers: get(this, "headers") })
    ).then((response) => {
      this.logResponse("PUT", response)
      return response
    })
  }

  del(url, opts) {
    return fetch(
      url,
      Object.assign({}, opts, {
        method: "DELETE",
        headers: get(this, "headers"),
      })
    ).then((response) => {
      this.logResponse("DELETE", response)
      return response.json()
    })
  }

  delRaw(url, opts) {
    return fetch(
      url,
      Object.assign({}, opts, {
        method: "DELETE",
        headers: get(this, "headers"),
      })
    ).then((response) => {
      this.logResponse("DELETE", response)
      return response
    })
  }
}
